import {
  GroupedSelectGroup,
  GroupedSelectOption,
} from '@/components/Form/GroupedSelect';

import {
  TaxEntryType,
  TaxInfoEntry,
  TaxInfoRequestPayload,
  TaxNumberTypeCode,
} from '@/types/api/tax-info';
import { TranslatorFunction } from '@/types/i18n';

import {
  getNoTaxNumberReasonCategoryForCode,
  isNoTaxNumberReasonCategory,
  isNoTaxNumberReasonCode,
} from '../ForeignNoTaxNumberReasonSection/utils';
import {
  QUESTION_2_TAX_ENTRY_TYPES,
  replaceOwnedTaxEntries,
} from '../utils/taxInfoStatus';
import {
  ForeignTaxResidencyBlockValues,
  OtherCountriesTaxFormValues,
} from '../utils/types';
import {
  FOREIGN_TAX_NUMBER_TYPE_CODES,
  FOREIGN_TAX_NUMBER_TYPE_OPTION_CODES,
} from './constants';

export function getForeignTaxNumberTypeGroupedSelectConfig(
  t: TranslatorFunction,
): {
  groups: GroupedSelectGroup[];
  otherOption: GroupedSelectOption;
} {
  return {
    groups: [
      {
        options: FOREIGN_TAX_NUMBER_TYPE_OPTION_CODES.map(code => ({
          value: code,
          label: t(
            `taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.options.${code}`,
          ),
          description: t(
            `taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.guidance.${code}`,
          ),
        })),
      },
    ],
    otherOption: {
      value: TaxNumberTypeCode.OTHER,
      label: t(
        `taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.options.${TaxNumberTypeCode.OTHER}`,
      ),
      description: t(
        `taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.guidance.${TaxNumberTypeCode.OTHER}`,
      ),
    },
  };
}

export const DEFAULT_FOREIGN_TAX_RESIDENCY_BLOCK: ForeignTaxResidencyBlockValues =
  {
    countryCode: '',
    taxNumberTypeCode: '',
    otherTaxNumberType: '',
    taxNumberValue: '',
    noTaxNumber: false,
    noTaxNumberReasonCategory: '',
    noTaxNumberReasonCode: '',
    noTaxNumberReasonOther: '',
  };

function isForeignTaxNumberTypeCode(
  code?: string,
): code is (typeof FOREIGN_TAX_NUMBER_TYPE_CODES)[number] {
  return FOREIGN_TAX_NUMBER_TYPE_CODES.some(
    foreignTaxNumberTypeCode => foreignTaxNumberTypeCode === code,
  );
}

export function mapTaxInfoEntryToForeignTaxResidencyBlock(
  entry: TaxInfoEntry,
): ForeignTaxResidencyBlockValues {
  const taxNumberTypeCode = isForeignTaxNumberTypeCode(entry.taxNumberTypeCode)
    ? entry.taxNumberTypeCode
    : '';
  const hasTaxNumberDetails =
    !!taxNumberTypeCode || !!entry.taxNumberValue?.trim();
  const hasNoTaxNumberReason =
    !!entry.noTaxNumberReasonCategory?.trim() ||
    !!entry.noTaxNumberReasonCode?.trim();
  const noTaxNumberReasonCategory =
    (isNoTaxNumberReasonCategory(entry.noTaxNumberReasonCategory)
      ? entry.noTaxNumberReasonCategory
      : getNoTaxNumberReasonCategoryForCode(entry.noTaxNumberReasonCode)) || '';
  const noTaxNumberReasonCode = isNoTaxNumberReasonCode(
    entry.noTaxNumberReasonCode,
  )
    ? entry.noTaxNumberReasonCode
    : '';

  return {
    countryCode: entry.countryCode ?? '',
    taxNumberTypeCode,
    otherTaxNumberType: entry.otherTaxNumberType ?? '',
    taxNumberValue: entry.taxNumberValue ?? '',
    noTaxNumber: hasNoTaxNumberReason || !hasTaxNumberDetails,
    noTaxNumberReasonCategory,
    noTaxNumberReasonCode,
    noTaxNumberReasonOther: entry.noTaxNumberReasonOther ?? '',
  };
}

export function getForeignTaxResidencyEntries(taxInfo?: TaxInfoEntry[]) {
  return (
    taxInfo?.filter(
      entry => entry.taxEntryType === TaxEntryType.FOREIGN_TAX_RESIDENCY,
    ) ?? []
  );
}

function buildForeignTaxResidencyEntry(
  block: ForeignTaxResidencyBlockValues,
  hasResidenceInOtherCountry: boolean,
): TaxInfoEntry {
  const entry: TaxInfoEntry = {
    countryCode: block.countryCode,
    taxEntryType: TaxEntryType.FOREIGN_TAX_RESIDENCY,
    isCompleted: true,
    hasResidenceInOtherCountry,
  };

  if (block.noTaxNumber) {
    entry.noTaxNumberReasonCategory =
      block.noTaxNumberReasonCategory || undefined;
    entry.noTaxNumberReasonCode = block.noTaxNumberReasonCode || undefined;
    entry.noTaxNumberReasonOther = block.noTaxNumberReasonOther || undefined;

    return entry;
  }

  entry.taxNumberTypeCode = block.taxNumberTypeCode || undefined;
  entry.taxNumberValue = block.taxNumberValue;

  if (block.taxNumberTypeCode === TaxNumberTypeCode.OTHER) {
    entry.otherTaxNumberType = block.otherTaxNumberType || undefined;
  }

  return entry;
}

export function buildForeignTaxResidencyPayload(
  formValues: OtherCountriesTaxFormValues,
  existingTaxInfo?: TaxInfoEntry[],
): TaxInfoRequestPayload {
  const residencyEntries = (formValues.foreignTaxResidencies ?? []).map(block =>
    buildForeignTaxResidencyEntry(block, formValues.hasForeignIndicia === true),
  );
  return replaceOwnedTaxEntries(existingTaxInfo ?? [], residencyEntries, [
    ...QUESTION_2_TAX_ENTRY_TYPES,
  ]);
}

export function shouldShowForeignTaxResidencyForm(
  formValues: OtherCountriesTaxFormValues,
): boolean {
  return (
    typeof formValues.hasForeignIndicia === 'boolean' &&
    formValues.isForeignTaxResident === true
  );
}
